import React from 'react';
import Icon from '../../../components/AppIcon';

const RegistrationChecklist = ({ checklistItems }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <Icon name="CheckCircle" size={20} color="var(--color-success)" />;
      case 'pending':
        return <Icon name="Clock" size={20} color="var(--color-warning)" />;
      case 'incomplete':
        return <Icon name="XCircle" size={20} color="var(--color-error)" />;
      default:
        return <Icon name="Circle" size={20} color="var(--color-muted-foreground)" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'pending':
        return 'Pending';
      case 'incomplete':
        return 'Incomplete';
      default:
        return 'Not Started';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success';
      case 'pending':
        return 'text-warning';
      case 'incomplete':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const completedCount = checklistItems?.filter(item => item?.status === 'completed')?.length;
  const totalCount = checklistItems?.length;
  const completionPercentage = Math.round((completedCount / totalCount) * 100);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="ListChecks" size={20} color="var(--color-primary)" />
          Registration Checklist
        </h3>
        <span className="text-sm font-medium text-primary">
          {completedCount}/{totalCount} Complete
        </span>
      </div>
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Overall Progress</span>
          <span className="text-sm font-semibold text-primary">{completionPercentage}%</span>
        </div>
        <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary to-primary/80 transition-all duration-500"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
      </div>
      {/* Checklist Items */}
      <div className="space-y-3">
        {checklistItems?.map((item) => (
          <div
            key={item?.id}
            className={`p-4 rounded-lg border transition-all duration-200 ${
              item?.status === 'completed'
                ? 'bg-success/5 border-success/20'
                : item?.status === 'pending' ?'bg-warning/5 border-warning/20'
                : item?.status === 'incomplete' ?'bg-error/5 border-error/20' :'bg-muted/30 border-border'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {getStatusIcon(item?.status)}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h4 className="font-medium text-foreground">{item?.title}</h4>
                  <span className={`text-xs font-medium ${getStatusColor(item?.status)}`}>
                    {getStatusText(item?.status)}
                  </span>
                </div>

                <p className="text-sm text-muted-foreground mb-2">{item?.description}</p>

                {item?.details && (
                  <div className="text-xs text-muted-foreground">
                    {item?.details}
                  </div>
                )}

                {item?.status === 'incomplete' && item?.action && (
                  <button
                    onClick={item?.action}
                    className="mt-2 text-xs font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
                  >
                    <Icon name="ArrowRight" size={14} />
                    Complete this step
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Summary Message */}
      {completionPercentage === 100 ? (
        <div className="mt-6 p-4 bg-success/10 border border-success/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="CheckCircle" size={20} color="var(--color-success)" />
            <div>
              <p className="font-medium text-success mb-1">All requirements completed!</p>
              <p className="text-sm text-foreground">
                You can now proceed with the final submission of your team registration.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-6 p-4 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={20} color="var(--color-warning)" />
            <div>
              <p className="font-medium text-warning mb-1">Action required</p>
              <p className="text-sm text-foreground">
                Please complete all pending items before submitting your registration.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationChecklist;